import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, Info } from 'lucide-react';
import { isAllowedPayUrl, type InvoiceDetail } from '@portal-contract';
import { Alert, AlertDescription, buttonVariants, TableBase, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/lib/macondo';
import { Btn } from '@/components/Btn';
import { useI18n } from '@/i18n/I18nProvider';
import { api } from '@/api/client';
import { ApiClientError } from '@/api/errors';
import { useInvoice } from '@/api/queries';
import { useIsWide } from '@/hooks/useMediaQuery';
import { usePdfDownload } from '@/hooks/usePdfDownload';
import { formatAmount, formatDate, formatMoney, formatNumber } from '@/lib/format';
import { PageHeading } from '@/components/PageHeading';
import { Card, Section } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { ErrorState, LoadingState } from '@/components/States';

/** `/a/:cid/invoices/:iid`: lines, tax, totals, payments applied, PDF, and the right payment note for this invoice. */
export function InvoiceDetailScreen() {
  const { cid = '', iid = '' } = useParams();
  const { t } = useI18n();
  const q = useInvoice(cid, iid);
  const back = (
    <Link to={`/a/${encodeURIComponent(cid)}/invoices`} className="focus-ring text-callout text-typography-primary inline-flex min-h-[44px] w-fit items-center gap-100 underline print:hidden">
      <ArrowLeft size={16} aria-hidden="true" />
      {t('common.back')}
    </Link>
  );
  if (q.isPending)
    return (
      <div className="flex flex-col gap-400" data-screen="InvoiceDetailScreen">
        {back}
        <LoadingState rows={4} />
      </div>
    );
  if (q.isError)
    return (
      <div className="flex flex-col gap-400" data-screen="InvoiceDetailScreen">
        {back}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </div>
    );
  return <Detail cid={cid} invoice={q.data} back={back} />;
}

function Detail({ cid, invoice: inv, back }: { cid: string; invoice: InvoiceDetail; back: React.ReactNode }) {
  const { t, lang } = useI18n();
  const wide = useIsWide();
  const pdf = usePdfDownload();
  const pay = inv.pay;
  const payUrl = pay?.kind === 'link' && isAllowedPayUrl(pay.url) ? pay.url : null;
  const currency = inv.total.currency;

  const pdfError = pdf.error instanceof ApiClientError && pdf.error.code === 'pdf_not_available' ? null : pdf.error;
  const pdfMissing = !inv.pdfAvailable || (pdf.error instanceof ApiClientError && pdf.error.code === 'pdf_not_available');

  return (
    <div className="flex flex-col gap-500" data-screen="InvoiceDetailScreen">
      {back}
      <div className="flex flex-wrap items-start justify-between gap-300">
        <div className="flex flex-col gap-200">
          <PageHeading>{t('invoice.heading', { number: inv.docNumber })}</PageHeading>
          <StatusBadge invoice={inv} />
        </div>
        <div className="flex flex-wrap items-center gap-300 print:hidden">
          {inv.pdfAvailable && (
            <Btn
              type="button"
              variant="secondary"
              size="lg"
              className="min-h-[48px]"
              loading={pdf.pending}
              disabled={pdf.pending}
              onClick={() => void pdf.run(() => api.invoicePdf(cid, inv.id), `invoice-${inv.docNumber}.pdf`)}
            >
              <Download size={18} aria-hidden="true" />
              {t('common.download')}
            </Btn>
          )}
          {payUrl && pay?.kind === 'link' && (
            <a
              href={payUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${buttonVariants({ size: 'lg' })} text-callout-medium min-h-[48px]`}
            >
              {t('invoice.payNow')}
              <span aria-hidden="true" className="tabular-nums">
                {formatMoney(pay.amount, lang)}
              </span>
              <ExternalLink size={16} aria-hidden="true" />
            </a>
          )}
        </div>
      </div>

      {pdfMissing && (
        <p className="text-callout text-typography-secondary" role="status">
          {t('invoice.pdfPending')}
        </p>
      )}
      {pdfError ? <ErrorState error={pdfError} className="print:hidden" /> : null}

      {payUrl && <p className="text-footnote text-typography-secondary -mt-300">{t('invoice.payNowNote')}</p>}
      {pay?.kind === 'stripe_managed' && <Note>{t('invoice.stripeManaged')}</Note>}
      {pay?.kind === 'contact' && <Note>{t('invoice.contactToPay')}</Note>}

      <Card className="p-500">
        <dl className="grid grid-cols-1 gap-x-600 gap-y-400 sm:grid-cols-2">
          <Meta label={t('invoice.date')} value={formatDate(inv.txnDate, lang)} />
          <Meta label={t('invoice.due')} value={inv.dueDate ? formatDate(inv.dueDate, lang) : '-'} />
          {inv.terms && <Meta label={t('invoice.terms')} value={inv.terms} />}
          <div className="sm:col-span-2">
            <dt className="text-footnote text-typography-secondary">{t('invoice.billTo')}</dt>
            <dd className="text-body text-typography-primary break-words">
              <span className="font-medium">{inv.billTo.name}</span>
              {inv.billTo.address.map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            </dd>
          </div>
        </dl>
      </Card>

      <Section id="lines-h" title={t('invoice.lines')}>
        {wide ? (
          <Card className="overflow-hidden">
            <TableBase>
              <TableCaption className="sr-only">{t('invoice.lines')}</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">{t('invoice.col.description')}</TableHead>
                  <TableHead scope="col" className="text-right">
                    {t('invoice.col.qty')}
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    {t('invoice.col.rate')}
                  </TableHead>
                  <TableHead scope="col" className="text-right">
                    {t('invoice.col.amount')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inv.lines.map((l, i) => (
                  <TableRow key={i}>
                    <TableCell className="whitespace-normal break-words">{l.description}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.quantity === null ? '-' : l.quantity.toLocaleString(lang === 'es' ? 'es-419' : 'en-US')}</TableCell>
                    <TableCell className="text-right tabular-nums">{l.unitPrice === null ? '-' : formatNumber(l.unitPrice, lang)}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums">{formatNumber(l.amount, lang)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TableBase>
          </Card>
        ) : (
          <ul className="flex flex-col gap-200">
            {inv.lines.map((l, i) => (
              <li key={i}>
                <Card className="p-400">
                  <p className="text-callout text-typography-primary break-words">{l.description}</p>
                  <p className="text-footnote text-typography-secondary mt-100 tabular-nums">
                    {l.quantity !== null && l.unitPrice !== null ? `${l.quantity.toLocaleString(lang === 'es' ? 'es-419' : 'en-US')} x ${formatNumber(l.unitPrice, lang)}` : ''}
                  </p>
                  <p className="text-callout-semibold text-typography-primary mt-100 text-right tabular-nums">{formatAmount(l.amount, currency, lang)}</p>
                </Card>
              </li>
            ))}
          </ul>
        )}

        <Card className="mt-300 ml-auto w-full max-w-[420px] p-500">
          <dl className="text-callout flex flex-col gap-200">
            <Total label={t('invoice.subtotal')} value={formatMoney(inv.subtotal, lang)} />
            <Total label={t('invoice.tax')} value={formatMoney(inv.tax, lang)} />
            <Total label={t('invoice.total')} value={formatMoney(inv.total, lang)} strong />
            <Total label={t('invoice.balance')} value={formatMoney(inv.balance, lang)} strong />
          </dl>
        </Card>
      </Section>

      {inv.memo && (
        <Card className="p-500">
          <h2 className="text-callout-medium text-typography-secondary">{t('invoice.memo')}</h2>
          <p className="text-body text-typography-primary mt-100 break-words whitespace-pre-wrap">{inv.memo}</p>
        </Card>
      )}

      {inv.payments.length > 0 && (
        <Section id="applied-h" title={t('invoice.payments')}>
          <ul className="flex flex-col gap-200">
            {inv.payments.map((p) => (
              <li key={p.id}>
                <Card className="text-callout flex items-center justify-between gap-300 p-400">
                  <span>{formatDate(p.date, lang)}</span>
                  <span className="text-callout-semibold tabular-nums">{formatMoney(p.amount, lang)}</span>
                </Card>
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Note({ children }: { children: string }) {
  return (
    <Alert tone="info" role="note" className="print:hidden">
      <div className="flex items-start gap-300">
        <Info size={20} aria-hidden="true" className="mt-050 shrink-0" />
        <AlertDescription>{children}</AlertDescription>
      </div>
    </Alert>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-footnote text-typography-secondary">{label}</dt>
      <dd className="text-body text-typography-primary">{value}</dd>
    </div>
  );
}

function Total({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-300">
      <dt className={strong ? 'text-callout-semibold text-typography-primary' : 'text-typography-secondary'}>{label}</dt>
      <dd className={`tabular-nums ${strong ? 'text-callout-semibold text-typography-primary' : 'text-typography-primary'}`}>{value}</dd>
    </div>
  );
}
